import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import DetailDialog from '../components/DetailDialog';

interface Area {
  id: string;
  area: string;
  translation: string;
  created_at: string;
  city_count?: number;
  company_count?: number;
}

interface City {
  id: string;
  city: string;
  translation: string;
  area_id: string;
  created_at: string;
  company_count?: number;
}

const AreasAndCitiesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [areas, setAreas] = useState<Area[]>([]);
  const [citiesByArea, setCitiesByArea] = useState<Record<string, City[]>>({});
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all areas
      const { data: areasData, error: areasError } = await supabase
        .from('areas')
        .select('*')
        .order('area');

      if (areasError) throw areasError;

      // Fetch all cities
      const { data: citiesData, error: citiesError } = await supabase
        .from('cities')
        .select('*')
        .order('city');

      if (citiesError) throw citiesError;

      // Fetch companies with their city information
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('city_id, cities!inner(area_id)');

      if (companiesError) {
        console.warn('Could not fetch company counts:', companiesError);
      }

      // Count cities per area
      const cityCountMap: Record<string, number> = {};
      citiesData?.forEach((city: City) => {
        cityCountMap[city.area_id] = (cityCountMap[city.area_id] || 0) + 1;
      });

      // Count companies per area
      const companyCountByAreaMap: Record<string, number> = {};
      companiesData?.forEach((company: any) => {
        const areaId = company.cities?.area_id;
        if (areaId) {
          companyCountByAreaMap[areaId] = (companyCountByAreaMap[areaId] || 0) + 1;
        }
      });

      // Count companies per city
      const companyCountByCityMap: Record<string, number> = {};
      companiesData?.forEach((company: any) => {
        const cityId = company.city_id;
        if (cityId) {
          companyCountByCityMap[cityId] = (companyCountByCityMap[cityId] || 0) + 1;
        }
      });

      // Add city and company counts to areas
      const areasWithCounts = areasData?.map((area: Area) => ({
        ...area,
        city_count: cityCountMap[area.id] || 0,
        company_count: companyCountByAreaMap[area.id] || 0,
      })) || [];

      // Add company counts to cities and group by area
      const citiesGrouped: Record<string, City[]> = {};
      citiesData?.forEach((city: City) => {
        if (!citiesGrouped[city.area_id]) {
          citiesGrouped[city.area_id] = [];
        }
        citiesGrouped[city.area_id].push({
          ...city,
          company_count: companyCountByCityMap[city.id] || 0,
        });
      });

      setAreas(areasWithCounts);
      setCitiesByArea(citiesGrouped);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditArea = (id: string) => {
    setSelectedAreaId(id);
    setAreaDialogOpen(true);
  };

  const handleEditCity = (cityId: string) => {
    setSelectedCityId(cityId);
    setCityDialogOpen(true);
  };

  // Area fields for dialog
  const areaFields = [
    {
      name: 'area',
      label: t('areasAndCities.areaNameEn'),
      type: 'text' as const,
      required: true,
    },
    {
      name: 'translation',
      label: t('areasAndCities.areaNameAr'),
      type: 'text' as const,
      required: true,
    },
  ];

  // City fields for dialog
  const cityFields = [
    {
      name: 'area_id',
      label: t('areasAndCities.area'),
      type: 'select' as const,
      required: true,
      disabled: true,
      options: areas.map(area => ({
        value: area.id,
        label: isRTL ? area.translation : area.area,
      })),
    },
    {
      name: 'city',
      label: t('areasAndCities.cityNameEn'),
      type: 'text' as const,
      required: true,
    },
    {
      name: 'translation',
      label: t('areasAndCities.cityNameAr'),
      type: 'text' as const,
      required: true,
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {t('areasAndCities.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('areasAndCities.subtitle')}
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
        {t('areasAndCities.infoMessage')}
      </Alert>

      {/* Areas with their cities in columns */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: 1.5,
          mb: 2,
        }}
      >
        {areas.map((area) => (
          <Box key={area.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Area Card */}
            <Card 
              sx={{
                transition: 'transform 0.15s, box-shadow 0.15s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {isRTL ? area.translation : area.area}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleEditArea(area.id)}
                    sx={{ p: 0.25 }}
                  >
                    <Edit fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {area.company_count || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('areasAndCities.companiesCount')}
                </Typography>
                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    {area.city_count || 0} {t('areasAndCities.citiesCount')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Cities under this area */}
            {citiesByArea[area.id]?.map((city) => (
              <Card 
                key={city.id}
                sx={{
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                  },
                }}
              >
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {isRTL ? city.translation : city.city}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleEditCity(city.id)}
                      sx={{ p: 0.25 }}
                    >
                      <Edit fontSize="small" sx={{ fontSize: '0.875rem' }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {city.company_count || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('areasAndCities.companiesCount')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ))}
      </Box>

      {/* Area Dialog */}
      <DetailDialog
        open={areaDialogOpen}
        onClose={() => setAreaDialogOpen(false)}
        tableName="areas"
        recordId={selectedAreaId || undefined}
        onSave={fetchData}
        fields={areaFields}
      />

      {/* City Dialog */}
      <DetailDialog
        open={cityDialogOpen}
        onClose={() => setCityDialogOpen(false)}
        tableName="cities"
        recordId={selectedCityId || undefined}
        onSave={fetchData}
        fields={cityFields}
      />
    </Box>
  );
};

export default AreasAndCitiesPage;
